import { useEffect, useId, useState } from "react";
import { AlertCircle, Info, Loader2, X } from "lucide-react";
import { cn } from "@/lib/cn";
import { Button } from "@/components/ui/button";
import { apiFetch } from "@/api/client";
import { useComplaintAction, useDeleteComplaint } from "@/hooks/useComplaints";
import type {
  ComplaintDetailResponse,
  ComplaintSeverity,
} from "@/api/types";
import { SEVERITY_LABEL, RESOLUTION_TYPE_LABEL, formatMoney } from "./constants";

interface BaseProps {
  complaintId: string;
  open: boolean;
  onClose: () => void;
  onDone?: () => void;
}

// ── shared shell ─────────────────────────────────────────────────────────────

function ModalShell({
  open,
  onClose,
  title,
  children,
  footer,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  footer: React.ReactNode;
}) {
  const headingId = useId();
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
      aria-hidden
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={headingId}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md rounded-lg border bg-background shadow-2xl"
      >
        <div className="flex items-center justify-between border-b px-4 py-3">
          <h4 id={headingId} className="text-sm font-semibold">
            {title}
          </h4>
          <button
            type="button"
            onClick={onClose}
            aria-label="Закрити"
            className="rounded-md p-1 text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="space-y-3 px-4 py-4">{children}</div>
        <div className="flex items-center justify-end gap-2 border-t px-4 py-3">{footer}</div>
      </div>
    </div>
  );
}

function ErrorLine({ error }: { error: string | null }) {
  if (!error) return null;
  return (
    <p role="alert" className="flex items-center gap-1.5 text-xs text-destructive">
      <AlertCircle className="h-3.5 w-3.5" /> {error}
    </p>
  );
}

function errMsg(e: unknown): string {
  return (e as Error)?.message ?? "Сталася помилка";
}

// ── reason-required modal (reject / request-changes / revoke / reopen) ────────

export function ReasonModal({
  complaintId,
  open,
  onClose,
  onDone,
  action,
  title,
  label,
  minChars,
  maxChars,
  confirmLabel,
  destructive,
  hint,
  placeholder,
  validationText,
  description,
  extraBody,
}: BaseProps & {
  action: string;
  title: string;
  label: string;
  minChars: number;
  maxChars?: number;
  confirmLabel: string;
  destructive?: boolean;
  hint?: string;
  placeholder?: string;
  /** W7 — dynamic validation line under the field (e.g. "мінімум 10 символів"). */
  validationText?: string;
  /** W7 — "що буде після" explanation block above the field. */
  description?: string;
  extraBody?: Record<string, unknown>;
}) {
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);
  const taId = useId();
  const mut = useComplaintAction(complaintId);

  useEffect(() => {
    if (open) {
      setReason("");
      setError(null);
    }
  }, [open]);

  const trimmed = reason.trim();
  const ok = trimmed.length >= minChars && !mut.isPending;

  async function submit() {
    setError(null);
    try {
      await mut.mutateAsync({ action, body: { reason: trimmed, ...(extraBody ?? {}) } });
      onDone?.();
      onClose();
    } catch (e) {
      setError(errMsg(e));
    }
  }

  return (
    <ModalShell
      open={open}
      onClose={onClose}
      title={title}
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={mut.isPending}>
            Скасувати
          </Button>
          <Button variant={destructive ? "destructive" : "default"} onClick={submit} disabled={!ok}>
            {mut.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : confirmLabel}
          </Button>
        </>
      }
    >
      {description && (
        <div className="flex gap-2 rounded-md border bg-muted/30 px-3 py-2 text-[13px] text-foreground/80">
          <Info className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
          <span>{description}</span>
        </div>
      )}
      <label htmlFor={taId} className="block text-xs font-medium">
        {label} <span className="text-destructive">*</span>
      </label>
      <textarea
        id={taId}
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        rows={4}
        maxLength={maxChars}
        placeholder={placeholder}
        className={cn(
          "w-full rounded-md border px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
          trimmed.length > 0 && trimmed.length < minChars && "border-warning-foreground",
        )}
      />
      <p className="flex items-center justify-between text-xs">
        <span
          className={cn(
            validationText && trimmed.length > 0 && trimmed.length < minChars
              ? "text-warning-foreground"
              : "text-muted-foreground",
          )}
        >
          {validationText ?? hint ?? ""}
        </span>
        <span className={cn(trimmed.length >= minChars ? "text-primary" : "text-muted-foreground")}>
          {reason.length}/{maxChars ?? minChars}
        </span>
      </p>
      <ErrorLine error={error} />
    </ModalShell>
  );
}

// ── confirm modal (no input) ──────────────────────────────────────────────────

export function ConfirmModal({
  complaintId,
  open,
  onClose,
  onDone,
  action,
  title,
  body,
  confirmLabel,
  destructive,
}: BaseProps & {
  action: string;
  title: string;
  body: string;
  confirmLabel: string;
  destructive?: boolean;
}) {
  const [error, setError] = useState<string | null>(null);
  const mut = useComplaintAction(complaintId);

  useEffect(() => {
    if (open) setError(null);
  }, [open]);

  async function submit() {
    setError(null);
    try {
      await mut.mutateAsync({ action, body: {} });
      onDone?.();
      onClose();
    } catch (e) {
      setError(errMsg(e));
    }
  }

  return (
    <ModalShell
      open={open}
      onClose={onClose}
      title={title}
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={mut.isPending}>
            Скасувати
          </Button>
          <Button variant={destructive ? "destructive" : "default"} onClick={submit} disabled={mut.isPending}>
            {mut.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : confirmLabel}
          </Button>
        </>
      }
    >
      <p className="text-sm text-foreground/90">{body}</p>
      <ErrorLine error={error} />
    </ModalShell>
  );
}

// ── delete own draft (COMP-S3-FIX W4) ─────────────────────────────────────────

/**
 * Author-only draft deletion. DELETE /:id (not a POST transition), so it uses its own
 * mutation rather than ConfirmModal's useComplaintAction. onDone closes the parent drawer
 * (the complaint is gone); the hook invalidates the list so the row disappears.
 */
export function DeleteDraftModal({
  complaintId,
  open,
  onClose,
  onDone,
}: BaseProps) {
  const [error, setError] = useState<string | null>(null);
  const mut = useDeleteComplaint(complaintId);

  useEffect(() => {
    if (open) setError(null);
  }, [open]);

  async function submit() {
    setError(null);
    try {
      await mut.mutateAsync();
      onDone?.();
      onClose();
    } catch (e) {
      setError(errMsg(e));
    }
  }

  return (
    <ModalShell
      open={open}
      onClose={onClose}
      title="Видалити чернетку"
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={mut.isPending}>
            Скасувати
          </Button>
          <Button variant="destructive" onClick={submit} disabled={mut.isPending}>
            {mut.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Видалити"}
          </Button>
        </>
      }
    >
      <p className="text-sm text-foreground/90">
        Чернетку буде видалено без можливості відновлення. Подану рекламацію видалити не можна.
      </p>
      <ErrorLine error={error} />
    </ModalShell>
  );
}

// ── approve financial resolution ──────────────────────────────────────────────

export function ApproveFinancialModal({
  complaintId,
  open,
  onClose,
  onDone,
  proposal,
}: BaseProps & { proposal: ComplaintDetailResponse["proposal"] }) {
  const [error, setError] = useState<string | null>(null);
  const mut = useComplaintAction(complaintId);

  useEffect(() => {
    if (open) setError(null);
  }, [open]);

  async function submit() {
    setError(null);
    try {
      await mut.mutateAsync({ action: "approve-financial", body: {} });
      onDone?.();
      onClose();
    } catch (e) {
      setError(errMsg(e));
    }
  }

  return (
    <ModalShell
      open={open}
      onClose={onClose}
      title="Погодити фінансове рішення"
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={mut.isPending}>
            Скасувати
          </Button>
          <Button onClick={submit} disabled={mut.isPending}>
            {mut.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Погодити"}
          </Button>
        </>
      }
    >
      {proposal && (
        <div className="rounded-md border bg-muted/30 p-3 text-sm">
          <div className="font-medium">{RESOLUTION_TYPE_LABEL[proposal.type]}</div>
          {proposal.amount != null && (
            <div className="mt-0.5">Сума: {formatMoney(proposal.amount, proposal.currency)}</div>
          )}
          {proposal.description && (
            <p className="mt-1 text-[13px] text-muted-foreground">{proposal.description}</p>
          )}
          {proposal.snapshotSha256 && (
            <p className="mt-1.5 text-[12px] text-muted-foreground">
              Доказів: {proposal.evidence.length} · SHA-256 {proposal.snapshotSha256.slice(0, 12)}…
            </p>
          )}
        </div>
      )}
      <p className="text-xs text-muted-foreground">
        Цілісність доказів буде повторно перевірено перед погодженням.
      </p>
      <ErrorLine error={error} />
    </ModalShell>
  );
}

// ── escalate (level + reason) ─────────────────────────────────────────────────

export function EscalateModal({ complaintId, open, onClose, onDone }: BaseProps) {
  const [level, setLevel] = useState<"supervisor" | "director">("supervisor");
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);
  const mut = useComplaintAction(complaintId);

  useEffect(() => {
    if (open) {
      setLevel("supervisor");
      setReason("");
      setError(null);
    }
  }, [open]);

  const ok = reason.trim().length >= 10 && !mut.isPending;

  async function submit() {
    setError(null);
    try {
      await mut.mutateAsync({ action: "escalate", body: { level, reason: reason.trim() } });
      onDone?.();
      onClose();
    } catch (e) {
      setError(errMsg(e));
    }
  }

  return (
    <ModalShell
      open={open}
      onClose={onClose}
      title="Передати керівнику"
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={mut.isPending}>
            Скасувати
          </Button>
          <Button onClick={submit} disabled={!ok}>
            {mut.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Передати керівнику"}
          </Button>
        </>
      }
    >
      <p className="text-[13px] text-muted-foreground">
        Керівник побачить рекламацію як ескальовану й зможе долучитися до рішення.
      </p>
      <div className="flex gap-2">
        {(["supervisor", "director"] as const).map((l) => (
          <button
            key={l}
            type="button"
            onClick={() => setLevel(l)}
            className={cn(
              "flex-1 rounded-md border px-3 py-2 text-sm",
              level === l ? "border-primary bg-primary/10 text-primary" : "border-border",
            )}
          >
            {l === "supervisor" ? "Супервайзер" : "Директор"}
          </button>
        ))}
      </div>
      <textarea
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        rows={3}
        placeholder="Причина передачі керівнику…"
        className="w-full rounded-md border px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      />
      <ErrorLine error={error} />
    </ModalShell>
  );
}

// ── close (satisfaction + reason) ─────────────────────────────────────────────

const SATISFACTION = [
  ["very_satisfied", "Дуже задоволений"],
  ["satisfied", "Задоволений"],
  ["neutral", "Нейтрально"],
  ["dissatisfied", "Незадоволений"],
  ["very_dissatisfied", "Дуже незадоволений"],
] as const;

export function CloseModal({ complaintId, open, onClose, onDone }: BaseProps) {
  const [satisfaction, setSatisfaction] = useState("");
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);
  const mut = useComplaintAction(complaintId);

  useEffect(() => {
    if (open) {
      setSatisfaction("");
      setReason("");
      setError(null);
    }
  }, [open]);

  async function submit() {
    setError(null);
    try {
      await mut.mutateAsync({
        action: "close",
        body: { customerSatisfaction: satisfaction || undefined, closureReason: reason.trim() },
      });
      onDone?.();
      onClose();
    } catch (e) {
      setError(errMsg(e));
    }
  }

  return (
    <ModalShell
      open={open}
      onClose={onClose}
      title="Закрити рекламацію"
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={mut.isPending}>
            Скасувати
          </Button>
          <Button onClick={submit} disabled={mut.isPending}>
            {mut.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Закрити"}
          </Button>
        </>
      }
    >
      <label className="block text-xs font-medium">Задоволеність клієнта</label>
      <select
        value={satisfaction}
        onChange={(e) => setSatisfaction(e.target.value)}
        className="h-9 w-full rounded-md border bg-background px-2 text-sm"
      >
        <option value="">— не вказано —</option>
        {SATISFACTION.map(([v, l]) => (
          <option key={v} value={v}>
            {l}
          </option>
        ))}
      </select>
      <textarea
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        rows={3}
        placeholder="Підсумок закриття (необов’язково)…"
        className="w-full rounded-md border px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      />
      <ErrorLine error={error} />
    </ModalShell>
  );
}

// ── severity change (newSeverity + reason) ────────────────────────────────────

export function SeverityModal({
  complaintId,
  open,
  onClose,
  onDone,
  current,
}: BaseProps & { current: ComplaintSeverity }) {
  const [sev, setSev] = useState<ComplaintSeverity>(current);
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);
  const mut = useComplaintAction(complaintId);

  useEffect(() => {
    if (open) {
      setSev(current);
      setReason("");
      setError(null);
    }
  }, [open, current]);

  const ok = reason.trim().length >= 10 && sev !== current && !mut.isPending;

  async function submit() {
    setError(null);
    try {
      await mut.mutateAsync({ action: "severity", body: { newSeverity: sev, reason: reason.trim() } });
      onDone?.();
      onClose();
    } catch (e) {
      setError(errMsg(e));
    }
  }

  return (
    <ModalShell
      open={open}
      onClose={onClose}
      title="Змінити важливість"
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={mut.isPending}>
            Скасувати
          </Button>
          <Button onClick={submit} disabled={!ok}>
            {mut.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Змінити"}
          </Button>
        </>
      }
    >
      <select
        value={sev}
        onChange={(e) => setSev(e.target.value as ComplaintSeverity)}
        className="h-9 w-full rounded-md border bg-background px-2 text-sm"
      >
        {(["low", "medium", "high", "critical"] as const).map((s) => (
          <option key={s} value={s}>
            {SEVERITY_LABEL[s]}
          </option>
        ))}
      </select>
      <textarea
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        rows={3}
        placeholder="Причина зміни важливості…"
        className="w-full rounded-md border px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      />
      <ErrorLine error={error} />
    </ModalShell>
  );
}

// ── SLA override (slaType + newSlaDueAt + reason >=100) ────────────────────────

export function SlaOverrideModal({ complaintId, open, onClose, onDone }: BaseProps) {
  const [slaType, setSlaType] = useState<"initial_response" | "resolution">("resolution");
  const [newSlaDueAt, setNewSlaDueAt] = useState("");
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);
  const mut = useComplaintAction(complaintId);

  useEffect(() => {
    if (open) {
      setSlaType("resolution");
      setNewSlaDueAt("");
      setReason("");
      setError(null);
    }
  }, [open]);

  const ok = newSlaDueAt !== "" && reason.trim().length >= 100 && !mut.isPending;

  async function submit() {
    setError(null);
    try {
      await mut.mutateAsync({
        action: "sla-override",
        body: { slaType, newSlaDueAt: newSlaDueAt.replace("T", " ") + ":00", reason: reason.trim() },
      });
      onDone?.();
      onClose();
    } catch (e) {
      setError(errMsg(e));
    }
  }

  return (
    <ModalShell
      open={open}
      onClose={onClose}
      title="Контрольні терміни"
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={mut.isPending}>
            Скасувати
          </Button>
          <Button onClick={submit} disabled={!ok}>
            {mut.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Зберегти"}
          </Button>
        </>
      }
    >
      <p className="text-[13px] text-muted-foreground">
        Тут можна змінити дедлайн першої відповіді або вирішення. Зміна записується в історію рекламації.
      </p>
      <div className="flex gap-2">
        {([
          ["initial_response", "Перша відповідь"],
          ["resolution", "Вирішення"],
        ] as const).map(([v, l]) => (
          <button
            key={v}
            type="button"
            onClick={() => setSlaType(v)}
            className={cn(
              "flex-1 rounded-md border px-3 py-2 text-sm",
              slaType === v ? "border-primary bg-primary/10 text-primary" : "border-border",
            )}
          >
            {l}
          </button>
        ))}
      </div>
      <label className="block text-xs font-medium">Новий термін</label>
      <input
        type="datetime-local"
        value={newSlaDueAt}
        onChange={(e) => setNewSlaDueAt(e.target.value)}
        className="h-9 w-full rounded-md border bg-background px-2 text-sm"
      />
      <textarea
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        rows={3}
        placeholder="Обґрунтування (мін. 100 символів — фіксується в аудиті)…"
        className="w-full rounded-md border px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      />
      <p className="text-right text-xs text-muted-foreground">{reason.trim().length}/100</p>
      <ErrorLine error={error} />
    </ModalShell>
  );
}

// ── manual assign / reassign (user picker) ────────────────────────────────────

export function AssignModal({ complaintId, open, onClose, onDone }: BaseProps) {
  const [query, setQuery] = useState("");
  const [users, setUsers] = useState<{ id: string; name: string }[]>([]);
  const [picked, setPicked] = useState<{ id: string; name: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const mut = useComplaintAction(complaintId);

  useEffect(() => {
    if (open) {
      setQuery("");
      setPicked(null);
      setError(null);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const t = window.setTimeout(async () => {
      try {
        const res = await apiFetch<{ list: { id: string; name: string }[] }>("/User", {
          query: {
            select: "name",
            maxSize: 12,
            orderBy: "name",
            "where[0][type]": query.trim() ? "textFilter" : "isTrue",
            "where[0][field]": query.trim() ? undefined : "isActive",
            "where[0][value]": query.trim() || undefined,
          },
        });
        setUsers(res.list ?? []);
      } catch {
        setUsers([]);
      }
    }, 250);
    return () => window.clearTimeout(t);
  }, [query, open]);

  async function submit() {
    if (!picked) return;
    setError(null);
    try {
      await mut.mutateAsync({ action: "assign", body: { assignedUserId: picked.id } });
      onDone?.();
      onClose();
    } catch (e) {
      setError(errMsg(e));
    }
  }

  return (
    <ModalShell
      open={open}
      onClose={onClose}
      title="Призначити відповідального"
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={mut.isPending}>
            Скасувати
          </Button>
          <Button onClick={submit} disabled={!picked || mut.isPending}>
            {mut.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Призначити"}
          </Button>
        </>
      }
    >
      <input
        autoFocus
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Пошук користувача…"
        className="h-9 w-full rounded-md border bg-background px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      />
      <ul className="max-h-52 overflow-y-auto rounded-md border">
        {users.length === 0 && (
          <li className="px-3 py-2 text-xs text-muted-foreground">Нічого не знайдено</li>
        )}
        {users.map((u) => (
          <li key={u.id}>
            <button
              type="button"
              onClick={() => setPicked(u)}
              className={cn(
                "flex w-full items-center px-3 py-2 text-left text-sm hover:bg-muted",
                picked?.id === u.id && "bg-primary/10 text-primary",
              )}
            >
              {u.name}
            </button>
          </li>
        ))}
      </ul>
      <ErrorLine error={error} />
    </ModalShell>
  );
}
